/**
 * 渲染层文件链接路径补全工具
 *
 * 规则：渲染时把 `/relay/cosfile` 补全为 `/file-center/relay/cosfile`
 *   - 已经是 `/file-center/relay/cosfile` 的保持不变，不重复叠加
 *   - TUS 直传链路 `/tus/relay/cosfile` 保持不变（补全会变成 /tus/file-center/... 的错误地址）
 *
 * 仅用于展示层（图片、链接、预览、下载地址），不参与请求体与响应体的改写。
 *
 * 实现说明：使用严格字符串匹配（indexOf + slice + endsWith），不使用正则。
 * 这样既避免了后行断言（lookbehind）在 Safari 16.4 以下抛 SyntaxError 导致白屏，
 * 也让匹配规则完全可读、可预期。
 */

// 需要补全的原始路径
const SOURCE_PATH = '/relay/cosfile';
// 补全后的目标路径
const TARGET_PATH = '/file-center/relay/cosfile';

// 匹配位置前如果以这些前缀结尾，则保持原样
const SKIP_PREFIXES = ['/file-center', '/tus'];

// 匹配位置之后必须是这些字符之一（或字符串末尾），否则不算命中
// 用于排除 /relay/cosfileXxx 这类只是前缀相似的路径
const VALID_NEXT_CHARS = ['/', '?', '#'];

// 日志开关：默认开启，控制台执行 window.__RELAY_COSFILE_LOG__ = false 可关闭
function isLogEnabled() {
  if (typeof window === 'undefined') return false;
  return window.__RELAY_COSFILE_LOG__ !== false;
}

function truncate(str, max = 300) {
  return str.length > max ? `${str.slice(0, max)}...` : str;
}

/**
 * 对单个字符串做路径补全
 * @param {string} str 原始地址（完整 URL 或相对路径）
 * @param {string} [scene] 仅用于日志标识来源场景
 * @returns {string} 处理后的地址，未命中时原样返回
 */
export function replaceRelayCosfile(str, scene) {
  if (typeof str !== 'string' || str.indexOf(SOURCE_PATH) === -1) {
    return str;
  }

  /**
   * 从 from 位置开始，找下一个合法的匹配起点
   * 合法性 = 匹配片段之后是字符串末尾或 / ? # 这些分隔符
   */
  const findValidIndex = (from) => {
    let pos = str.indexOf(SOURCE_PATH, from);
    while (pos !== -1) {
      const end = pos + SOURCE_PATH.length;
      if (end === str.length || VALID_NEXT_CHARS.indexOf(str[end]) !== -1) {
        return pos;
      }
      pos = str.indexOf(SOURCE_PATH, pos + 1);
    }
    return -1;
  };

  let result = '';
  let cursor = 0;
  let changed = false;
  let index = findValidIndex(cursor);

  while (index !== -1) {
    // 先原样搬运本次匹配之前的内容
    result += str.slice(cursor, index);

    // 用原串匹配位置之前的内容判断前缀，严格比对结尾
    const beforeMatch = str.slice(0, index);
    const shouldSkip = SKIP_PREFIXES.some((prefix) => beforeMatch.endsWith(prefix));

    if (shouldSkip) {
      result += SOURCE_PATH;
    } else {
      result += TARGET_PATH;
      changed = true;
    }

    cursor = index + SOURCE_PATH.length;
    index = findValidIndex(cursor);
  }

  // 尾部剩余内容
  result += str.slice(cursor);

  if (!changed) {
    return str;
  }

  if (isLogEnabled()) {
    const label = scene ? `urlTransform/${scene}` : 'urlTransform';
    console.log(
      `[${label}] 链接已替换\n  原始: ${truncate(str)}\n  替换后: ${truncate(result)}`
    );
  }
  return result;
}

export default replaceRelayCosfile;
